import {
  editable,
  moduleTypeSelect,
} from '@/src/tools/contentModulesFieldName';
import { replaceI18nVariables } from '@/src/tools/tools';
import {
  EPreviewSize,
  SalesChannelModeWithSister,
} from '@/src/types/common/SalesChannel';
import {
  EModuleWidth,
  EResponsiveProductToShow,
  EnumModuleType,
} from '@/src/types/common/module';
import { get, uniqBy } from 'lodash';
import { checkResponsiveProductCarousel } from 'src/constrains/SalesChannelContent-Revamp/ModuleList/validator';
import {
  animationFields,
  moduleStyleFields,
} from 'src/strategies/render/constant';
import {
  actionButtonTheme,
  autoPlayCheckbox,
  autoPlayInterval,
  leaveGap,
  moduleWidthSelect,
  products as pK,
  productToShow,
  scrollOut,
} from 'src/tools/contentModulesFieldName';
import i18n from '../i18n';

import {
  ResponsiveContainer,
  ResponsiveField,
  countsOfItems,
  countsOfItems_i18n,
} from '../util';
import {
  ANIMATION,
  CAROUSEL,
  MOUDLE_STYLE,
  PRODUCTS,
  TEXT,
  productTextFields,
} from './constant';
import { carouselTypeOptionsGenerator } from './tools';

export default (
  values: any,
  responsiveType: EPreviewSize,
  salesChannelMode: SalesChannelModeWithSister,
) => {
  const carouselSetting = getSettingTemplate(
    values,
    responsiveType,
    salesChannelMode,
  );
  const moduleStyleSetting = getModuleStyleTemplate(
    values,
    responsiveType,
    EnumModuleType.ResponsiveProductCarousel,
  );
  const animation = getAnimationTemplate(values, responsiveType);
  const products = getProductsTemplate(values, responsiveType);
  return [
    {
      title: i18n['general.content.carousel'],
      id: CAROUSEL,
      field: carouselSetting,
      fields: [
        [moduleWidthSelect],
        [autoPlayCheckbox],
        [autoPlayInterval],
        [productToShow],
        [leaveGap],
        [scrollOut],
        [actionButtonTheme],
      ],
    },
    {
      title: i18n['general.content.moduleStyle'],
      id: MOUDLE_STYLE,
      type: ResponsiveContainer.ModuleStyle,
      field: moduleStyleSetting,
      fields: moduleStyleFields,
    },
    {
      title: i18n['general.content.animation'],
      id: ANIMATION,
      field: animation,
      fields: animationFields,
    },
    {
      title: i18n['general.content.text'],
      id: TEXT,
      field: [
        {
          field: ResponsiveField.ProductText,
          disabled: !(
            responsiveType === EPreviewSize.WIDE &&
            !values[`${TEXT}_${editable}`]
          ),
        },
      ],
      fields: productTextFields,
    },
    {
      title: i18n['general.content.products'],
      id: PRODUCTS,
      field: products,
      fields: [[pK]],
      headerErrorDependencies: [productToShow, pK], //for section validation
      validator: (data, extra) => {
        const { checkProductsError } = checkResponsiveProductCarousel();
        return checkProductsError(data, extra);
      },
    },
  ];
};

export const not_editable = [
  moduleTypeSelect,
  moduleWidthSelect,
  autoPlayCheckbox,
  autoPlayInterval,
  ...animation_not_editable,
];

export const productToShowOptions = (breakpoint: EPreviewSize) => {
  switch (breakpoint) {
    case EPreviewSize.WIDE: {
      return [
        {
          label: replaceI18nVariables(
            i18n['general.producttoshow.one'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.One,
        },
        {
          label: replaceI18nVariables(
            i18n['general.producttoshow.twoportrait'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.TwoPortrait,
        },
        {
          label: replaceI18nVariables(
            i18n['general.producttoshow.twolandscape'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.TwoLandscape,
        },
        {
          label: replaceI18nVariables(
            i18n['general.producttoshow.three'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.Three,
        },
        {
          label: replaceI18nVariables(
            i18n['general.producttoshow.four'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.Four,
        },
        {
          label: replaceI18nVariables(
            i18n['general.producttoshow.five'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.Five,
        },
        {
          label: replaceI18nVariables(
            i18n['general.producttoshow.six'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.Six,
        },
      ];
    }
    default: {
      return [
        {
          label: replaceI18nVariables(
            i18n['general.producttoshow.one'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.One,
        },
        {
          label: replaceI18nVariables(
            i18n['general.producttoshow.twoportrait'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.TwoPortrait,
        },
        {
          label: replaceI18nVariables(
            i18n['general.producttoshow.twolandscape'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.TwoLandscape,
        },
        {
          label: replaceI18nVariables(
            i18n['general.producttoshow.three'],
            i18n['general.label.product'],
          ),
          value: EResponsiveProductToShow.Three,
        },
      ];
    }
  }
};

export const getSettingTemplate = (
  values: any,
  responsiveType: EPreviewSize,
  salesChannelMode: SalesChannelModeWithSister,
) => {
  const productOptions = productToShowOptions(responsiveType);
  const v =
    responsiveType === EPreviewSize.WIDE || values[`${CAROUSEL}_${editable}`];

  let commonFields: Array<any> = [
    {
      field: ResponsiveField.CarouselType,
      disabled: responsiveType !== EPreviewSize.WIDE || !v,
      options: carouselTypeOptionsGenerator({ salesChannelMode }),
    },
    {
      field: ResponsiveField.ModuleWidth,
      disabled: responsiveType !== EPreviewSize.WIDE || !v,
      options: [
        // { label: i18n['general.content.fullWidth'], value: EModuleWidth.FULL },
        { label: i18n['general.content.none'], value: EModuleWidth.NONE },
        { label: i18n['general.content.wideWidth'], value: EModuleWidth.WIDE },
      ],
    },
    {
      field: ResponsiveField.AutoPlay,
      disabled: responsiveType !== EPreviewSize.WIDE || !v,
    },
    {
      field: ResponsiveField.ProductToShow,
      disabled: !v,
      options: productOptions,
    },
  ];
  const productToShow = get(values, 'productToShow');
  if (productToShow !== EResponsiveProductToShow.One) {
    const count = countsOfItems(productToShow);
    commonFields = [
      ...commonFields,
      {
        field: ResponsiveField.LeaveGap,
        disabled: !v,
        options: uniqBy(
          productOptions.map((v) => {
            return {
              label: replaceI18nVariables(
                countsOfItems_i18n(v?.value),
                i18n['general.label.product'],
              ),
              value: countsOfItems(v?.value),
            };
          }),
          (e) => e.value,
        ).slice(0, count),
      },
    ];
  }
  commonFields = [
    ...commonFields,
    {
      field: ResponsiveField.CallToActionButton,
      disabled: !v,
    },
  ];
  return commonFields;
};

import {
  animation_not_editable,
  getModuleStyleTemplate,
} from './imageCarousel';
import { getAnimationTemplate } from './imageCarousel';

export const getProductsTemplate = (
  values: any,
  responsiveType: EPreviewSize,
) => {
  const v =
    responsiveType === EPreviewSize.WIDE || values[`${PRODUCTS}_${editable}`];
  return [
    {
      field: ResponsiveField.Products,
      disabled: !v,
    },
  ];
};
